import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import PropTypes from "prop-types";

import routes from "../../routes";
import useAuth from "../../hooks/useAuth";
import { LayoutContext } from "../../context/LayoutContext";
import BaseWrapper from "../../components/ui/BaseWrapper";
import {
  brandFontFamily,
  brandInputTextColor,
  brandPrimaryColor,
} from "../../utils/variables";
import WorkExperienceHeader from "../../views/WorkExperience/components/WorkExperienceHeader";

function CustomTabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`we-tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}
CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

const CustomTab = styled((props) => <Tab disableRipple {...props} />)(
  ({ theme }) => ({
    textTransform: "none",
    minWidth: 0,
    height: "38px",
    width: "230px",
    fontWeight: 300,
    fontSize: "16px",
    boxShadow: "0px -1px 8px 0px rgba(0,0,0,0.09)",
    borderRadius: "6px",
    margin: "20px 0",
    background: "white",
    marginRight: theme.spacing(1),
    color: brandInputTextColor,
    fontFamily: brandFontFamily,
    "&.Mui-selected": {
      color: brandPrimaryColor,
      fontWeight: 400,
      borderBottom: `1px solid ${brandPrimaryColor}`,
    },
  })
);

export default function WorkExperience() {
  const { isBannerVisible } = useContext(LayoutContext);
  const { authRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const { tabs = [] } = useMemo(() => {
    const manage = routes.find(
      (r) => r.path === "/admin/manage" || r.path === "manage"
    );
    const child = manage?.childs?.find(
      (c) => c.path && c.path.startsWith("workExperience")
    );
    return child || {};
  }, []);

  const visibleTabs = useMemo(
    () => tabs.filter((t) => t?.roles?.includes(authRole)),
    [tabs, authRole]
  );

  const [value, setValue] = useState(0);
  useEffect(() => {
    const idx = visibleTabs.findIndex((t) =>
      location.pathname.endsWith(`/manage/workExperience/${t.path}`)
    );

    if (idx !== -1) {
      setValue(idx);
    } else {
      setValue(0);
    }
  }, [location.pathname, visibleTabs]);

  const handleChange = (_e, newValue) => {
    const t = visibleTabs[newValue];
    navigate(`/manage/workExperience/${t.path}`);
    setValue(newValue);
  };

  return (
    <div className="bg-light">
      <div
        className={`bg-[#F6F6F6] sm:fixed 2xl:w-[95.2%] xl:w-[93.2%] lg:w-[90.6%] md:w-[87.5%] sm:w-[95%] w-full h-screen transition-all ${
          isBannerVisible ? "top-[106px]" : "top-[56px]"
        }`}
      >
        <WorkExperienceHeader />

        <BaseWrapper>
          <Tabs
            value={value}
            onChange={handleChange}
            TabIndicatorProps={{ style: { display: "none" } }}
            aria-label="work experience tabs"
          >
            {visibleTabs.map((t, i) => (
              <CustomTab
                key={t.path}
                label={t.text}
                id={`we-tab-${i}`}
                aria-controls={`we-tabpanel-${i}`}
                sx={{
                  minHeight: "38px",
                  marginRight: "40px",
                }}
              />
            ))}
          </Tabs>

          {visibleTabs.map((t, i) => (
            <CustomTabPanel key={t.path} value={value} index={i}>
              {t.component}
            </CustomTabPanel>
          ))}
        </BaseWrapper>
      </div>
    </div>
  );
}
